import type { UploadAiProduct, UploadRow } from '@/models/upload';
import { aiProductName } from './aiProductName';

const dicomLabel = ({ filesProcessed, filesTotal }: UploadRow): string =>
  filesTotal
    ? `Processing DICOMs · ${filesProcessed ?? 0}/${filesTotal}`
    : 'Processing DICOMs';

const isRunning = ({ status }: UploadAiProduct) =>
  status === 'UPLOADING' || status === 'SCANNING';

/**
 * `(n/m)` is the current product's position in `aiProducts`, not a count of
 * finished ones: `(3/3)` means the third of three is running.
 */
const aiLabel = ({ aiProducts }: UploadRow): string => {
  // No scan yet, or AI is disabled at this site: the study is viewable.
  if (aiProducts.length === 0) return 'Ready to view · AI pending';
  let index = aiProducts.findIndex(isRunning);
  if (index < 0) index = aiProducts.findIndex((p) => p.status === 'PENDING');
  if (index < 0) return 'AI processing';
  const name = aiProductName(aiProducts[index].product);
  return `AI processing · ${name} (${index + 1}/${aiProducts.length})`;
};

/** The Status cell text for an upload, by its stage (AP-899 R4). */
export function uploadStatusLabel(row: UploadRow): string {
  switch (row.stage) {
    case 'DICOM':
      return dicomLabel(row);
    case 'AI':
      return aiLabel(row);
    case 'DONE':
      return 'Done';
    case 'FAILED':
      return 'Failed';
    default:
      return '';
  }
}

export type UploadBadgeVariant =
  | 'processing'
  | 'failed'
  | 'success'
  | 'warning';

/** Failed AI products of an upload, by display name. */
export const failedProductNames = ({ aiProducts }: UploadRow): string[] =>
  aiProducts
    .filter((p) => p.status === 'FAILED')
    .map((p) => aiProductName(p.product));

/** The badge's `data-variant` (AP-899 R6). */
export function uploadBadgeVariant(row: UploadRow): UploadBadgeVariant {
  switch (row.stage) {
    case 'FAILED':
      return 'failed';
    case 'DONE':
      return row.error || failedProductNames(row).length > 0
        ? 'warning'
        : 'success';
    default:
      return 'processing';
  }
}

/**
 * The badge tooltip (AP-899 R7): the error and the failed products, for a
 * warning DONE row or a FAILED row with an error. Null when there is none.
 */
export function uploadStatusTooltip(row: UploadRow): string | null {
  const shows =
    uploadBadgeVariant(row) === 'warning' ||
    (row.stage === 'FAILED' && !!row.error);
  if (!shows) return null;
  const products = failedProductNames(row).join(', ');
  return [row.error, products].filter(Boolean).join(' · ');
}
